import React, { useState, useEffect, useCallback } from "react";
import { BiRefresh, BiDownload, BiCalendar, BiFilter, BiMoney, BiTrendingUp, BiUser, BiCheckCircle, BiError, BiInfoCircle } from "react-icons/bi";
import { useAnalyticsStore } from "../../../state/analytics";
import showToast from "../../../utils/toastUtils";
import { ThemeContext } from "../../../themes/ThemeContext";
import TableSection from "../../Dashboards/components/TableSection";
import { getAllPartners } from "../../../data/api/api";

const InternalSettlement = () => {
  const theme = React.useContext(ThemeContext);
  const [formData, setFormData] = useState({
    partnerId: '',
    amount: '',
    description: 'Internal admin settlement'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selectedPartnerForHistory, setSelectedPartnerForHistory] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({
    startDate: '',
    endDate: '',
    status: ''
  });
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState(null);

  const {
    settlementData,
    settlementLoading,
    settlementError,
    settlementHistory,
    settlementHistoryLoading,
    settlementHistoryError,
    settlementStats,
    settlementStatsLoading,
    settlementStatsError,
    selectedPartnerBalance,
    partnerBalanceLoading,
    partnerBalanceError,
    processInternalSettlement,
    fetchSettlementHistory,
    fetchSettlementsByPartner,
    fetchSettlementsByDate,
    fetchSettlementStats,
    fetchPartnerBalance,
    clearSettlementData
  } = useAnalyticsStore();

  useEffect(() => {
    fetchPartners();
    loadInitialData();
  }, []);

  const fetchPartners = async () => {
    setPartnersLoading(true);
    setPartnersError(null);
    try {
      const response = await getAllPartners(1, 100);
      if (response.status === 'success' && response.data?.partners) {
        setPartners(response.data.partners);
      } else {
        setPartnersError(response.message || 'Failed to load partners');
      }
    } catch (error) {
      setPartnersError(error.message || 'Failed to load partners');
    } finally {
      setPartnersLoading(false);
    }
  };

  const loadInitialData = useCallback(async () => {
    try {
      await Promise.all([
        fetchSettlementHistory(null, 1, perPage).catch(err => {
          // console.warn("Settlement history not available:", err.message);
          return { success: true, data: [] };
        }),
        fetchSettlementStats().catch(err => {
          // console.warn("Settlement stats not available:", err.message);
          return { success: true, data: {} };
        })
      ]);
    } catch (error) {
      // console.error("❌ Failed to load initial data:", error);
      showToast("error", "Failed to load initial data", 3000);
    }
  }, [fetchSettlementHistory, fetchSettlementStats, perPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartnerChange = async (e) => {
    const partnerId = e.target.value;
    setFormData(prev => ({
      ...prev,
      partnerId: partnerId
    }));

    // Fetch partner balance when partner is selected
    if (partnerId) {
      try {
        await fetchPartnerBalance(partnerId);
      } catch (error) {
        // console.error("Failed to fetch partner balance:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partnerId || !formData.amount) {
      showToast("error", "Please fill in all required fields", 3000);
      return;
    }

    const amount = parseInt(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      showToast("error", "Please enter a valid amount in kobo", 3000);
      return;
    }

    try {
      await processInternalSettlement(formData.partnerId, amount, formData.description);
      showToast("success", "Internal settlement processed successfully", 3000);
      
      // Refresh settlement history after successful settlement
      await fetchSettlementHistory(selectedPartnerForHistory || null, currentPage, perPage);
      
      // Clear form
      setFormData({
        partnerId: '',
        amount: '',
        description: 'Internal admin settlement'
      });
    } catch (error) {
      // console.error("❌ Settlement processing failed:", error);
      showToast("error", error.message || "Failed to process settlement", 3000);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([fetchPartners(), loadInitialData()]);
    showToast("success", "Data refreshed successfully", 2000);
  };

  const handleHistoryFilter = async () => {
    try {
      setCurrentPage(1);
      await fetchSettlementHistory(
        selectedPartnerForHistory || null, 
        1, 
        perPage, 
        historyFilters.startDate || null, 
        historyFilters.endDate || null, 
        historyFilters.status || null
      );
      showToast("success", "Settlement history filtered", 2000);
    } catch (error) {
      showToast("error", "Failed to filter settlement history", 3000);
    }
  };

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    try {
      await fetchSettlementHistory(
        selectedPartnerForHistory || null, 
        page, 
        perPage, 
        historyFilters.startDate || null, 
        historyFilters.endDate || null, 
        historyFilters.status || null
      );
      showToast("success", `Data loaded for page ${page}`, 2000);
    } catch (error) {
      showToast("error", "Failed to load data for the selected page", 3000);
    }
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    try {
      await fetchSettlementHistory(
        selectedPartnerForHistory || null, 
        1, 
        newPerPage, 
        historyFilters.startDate || null, 
        historyFilters.endDate || null, 
        historyFilters.status || null
      );
      showToast("success", `Records per page changed to ${newPerPage}`, 2000);
    } catch (error) {
      showToast("error", "Failed to change records per page", 3000);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount) || amount === '') {
      return 'N/A';
    }
    return `₦${parseFloat(amount).toLocaleString()}`;
  };

  const formatKobo = (amount) => {
    return `${amount.toLocaleString()} kobo`;
  };

  // Prepare settlement history table data
  const prepareHistoryTableData = () => {
    if (!settlementHistory?.data?.data) return [];
    
    return settlementHistory.data.data.map((settlement, index) => ({
      id: settlement.id || index + 1,
      partner_id: settlement.partner_id,
      partner_name: settlement.partner_name,
      account_number: settlement.account_number,
      amount: settlement.amount,
      description: settlement.description,
      transfer_reference: settlement.transfer_reference,
      transaction_reference: settlement.transaction_reference,
      payin_balance_before: settlement.payin_balance_before,
      payin_balance_after: settlement.payin_balance_after,
      payout_balance_before: settlement.payout_balance_before,
      payout_balance_after_transfer: settlement.payout_balance_after_transfer,
      payout_balance_after_settlement: settlement.payout_balance_after_settlement,
      status: settlement.status,
      processed_by: settlement.processed_by,
      created_at: settlement.created_at,
      updated_at: settlement.updated_at
    }));
  };

  // Define columns for settlement history table
  const historyTableColumns = [
    // {
    //   key: 'id',
    //   label: 'ID',
    //   format: (value) => `#${value}`,
    // },
    {
      key: 'partner_id',
      label: 'Partner ID',
    },
    {
      key: 'partner_name',
      label: 'Partner Name',
      format: (value) => value || '-',
    },
    {
      key: 'amount',
      label: 'Amount',
      format: (value) => formatCurrency(value),
    },
    {
      key: 'description',
      label: 'Description',
      format: (value) => value || '-',
    },
    {
      key: 'transfer_reference',
      label: 'Transfer Ref',
      format: (value) => (
        <span className="font-mono text-xs" title={value}>
          {value && value.length > 20 ? `${value.substring(0, 20)}...` : value}
        </span>
      ),
    },
    {
      key: 'transaction_reference',
      label: 'Transaction Ref',
      format: (value) => (
        <span className="font-mono text-xs" title={value}>
          {value && value.length > 20 ? `${value.substring(0, 20)}...` : value}
        </span>
      ),
    },
    {
      key: 'payin_balance_after',
      label: 'PayIn Balance After',
      format: (value) => formatCurrency(value),
    },
    {
      key: 'payout_balance_after_settlement',
      label: 'Payout Balance After',
      format: (value) => formatCurrency(value),
    },
    {
      key: 'status',
      label: 'Status',
      format: (value) => {
        if (value === 'completed') {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
              {value}
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
              {value}
            </span>
          );
        }
      },
    },
    {
      key: 'processed_by',
      label: 'Processed By',
      format: (value) => value || '-',
    },
    {
      key: 'created_at',
      label: 'Created At',
      format: (value) => new Date(value).toLocaleString(),
    },
  ];

  if (settlementLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: theme.colors.text.secondary }}>Loading settlement data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
            Internal Settlement
          </h2>
          <p className="text-gray-400">
            Process internal settlements for partners. This replicates the normal transaction flow without external API calls.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}
            title="Refresh data"
          >
            <BiRefresh className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Settlement Form */}
      <div className="rounded-lg p-6" style={{ backgroundColor: theme.colors.background.secondary }}>
        <h3 className="text-xl font-bold mb-6" style={{ color: theme.colors.text.primary }}>
          Process New Settlement
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Partner ID *
              </label>
              {partnersLoading ? (
                <div className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-400">
                  Loading partners...
                </div>
              ) : partnersError ? (
                <div className="w-full px-3 py-2 border border-red-600 rounded-lg bg-gray-700 text-red-400 text-sm">
                  {partnersError}
                </div>
              ) : partners.length > 0 ? (
                <select
                  name="partnerId"
                  value={formData.partnerId}
                  onChange={handlePartnerChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                  required
                >
                  <option value="">Select Partner</option>
                  {partners.map(partner => (
                    <option key={partner.partnerId} value={partner.partnerId}>
                      {partner.partnerId} - {partner.business_name || partner.first_name + ' ' + partner.last_name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="partnerId"
                  value={formData.partnerId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    borderColor: theme.colors.background.accent,
                    color: theme.colors.text.primary
                  }}
                  placeholder="Enter Partner ID"
                  required
                />
              )}
              <p className="text-xs text-gray-400 mt-1">
                {partners.length > 0 
                  ? "Select a partner from the list above"
                  : "Enter the partner ID manually (format: PART-XXXXXXXXX)"
                }
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                Amount (in kobo) *
              </label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                style={{
                  backgroundColor: theme.colors.background.primary,
                  borderColor: theme.colors.background.accent,
                  color: theme.colors.text.primary
                }}
                placeholder="25000000"
                required
                min="1"
              />

            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
              Description
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              style={{
                backgroundColor: theme.colors.background.primary,
                borderColor: theme.colors.background.accent,
                color: theme.colors.text.primary
              }}
              placeholder="Internal admin settlement"
            />
          </div>

          {/* Partner Balance Display */}
          {selectedPartnerBalance && (
            <div className="p-4 rounded-lg bg-blue-900/20 border border-blue-500/30">
              <div className="flex items-center gap-2 mb-2">
                <BiInfoCircle className="text-blue-400" />
                <span className="text-sm font-medium text-blue-400">Partner Balance Information</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">PayIn Balance:</span>
                  <span className="ml-2 font-medium text-white">
                    {formatCurrency(selectedPartnerBalance.payin_balance || 0)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Payout Balance:</span>
                  <span className="ml-2 font-medium text-white">
                    {formatCurrency(selectedPartnerBalance.payout_balance || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={settlementLoading}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-500 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}

            >
              {settlementLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <BiCheckCircle className="w-4 h-4" />
                  Process Settlement
                </>
              )}
            </button>
          </div>
        </form>

        {/* Settlement Result */}
        {settlementData && (
          <div className="mt-6 p-4 rounded-lg bg-green-900/20 border border-green-500/30">
            <div className="flex items-center gap-2 mb-4">
              <BiCheckCircle className="text-green-400" />
              <h4 className="text-lg font-semibold text-green-400">Settlement Completed Successfully!</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Partner ID:</span>
                <span className="ml-2 font-medium text-white">{settlementData.data?.partner_id}</span>
              </div>
              <div>
                <span className="text-gray-400">Amount:</span>
                <span className="ml-2 font-medium text-white">{formatCurrency(settlementData.data?.amount)}</span>
              </div>
              <div>
                <span className="text-gray-400">Transfer Reference:</span>
                <span className="ml-2 font-medium text-white font-mono text-xs">
                  {settlementData.data?.transfer_ref}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Transaction Reference:</span>
                <span className="ml-2 font-medium text-white font-mono text-xs">
                  {settlementData.data?.transaction_ref}
                </span>
              </div>
              <div>
                <span className="text-gray-400">PayIn Balance After:</span>
                <span className="ml-2 font-medium text-white">{formatCurrency(settlementData.data?.payin_balance_after)}</span>
              </div>
              <div>
                <span className="text-gray-400">Payout Balance After:</span>
                <span className="ml-2 font-medium text-white">{formatCurrency(settlementData.data?.payout_balance_after_settlement)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Settlement Error */}
        {settlementError && (
          <div className="mt-6 p-4 rounded-lg bg-red-900/20 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <BiError className="text-red-400" />
              <h4 className="text-lg font-semibold text-red-400">Settlement Failed</h4>
            </div>
            <p className="text-red-300">{settlementError}</p>
          </div>
        )}
      </div>

      {/* Settlement History */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-xl font-bold" style={{ color: theme.colors.text.primary }}>
              Settlement History
            </h3>
            <p className="text-gray-400">
              View all processed internal settlements with detailed transaction information.
            </p>
          </div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}

          >
            {showHistory ? 'Hide History' : 'Show History'}
          </button>
        </div>

        {showHistory && (
          <>
            {/* Settlement Statistics */}
            {settlementStats?.data && (
              <div className="space-y-6">
                <h4 className="text-lg font-bold" style={{ color: theme.colors.text.primary }}>
                  Settlement Statistics
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
                    <h5 className="text-sm font-medium text-gray-400 mb-2">Total Settlements</h5>
                    <p className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                      {settlementStats.data.total_settlements || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
                    <h5 className="text-sm font-medium text-gray-400 mb-2">Total Amount</h5>
                    <p className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                      {formatCurrency(settlementStats.data.total_amount || 0)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
                    <h5 className="text-sm font-medium text-gray-400 mb-2">Average Amount</h5>
                    <p className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                      {formatCurrency(settlementStats.data.average_amount || 0)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg" style={{ backgroundColor: theme.colors.background.secondary }}>
                    <h5 className="text-sm font-medium text-gray-400 mb-2">Unique Partners</h5>
                    <p className="text-2xl font-bold" style={{ color: theme.colors.text.primary }}>
                      {settlementStats.data.unique_partners || 0}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* History Filters */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2">
                  <BiFilter className="text-gray-400" />
                  <span className="text-sm text-gray-400">Partner:</span>
                  {partners.length > 0 ? (
                    <select
                      value={selectedPartnerForHistory}
                      onChange={(e) => setSelectedPartnerForHistory(e.target.value)}
                      className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      style={{
                        backgroundColor: theme.colors.background.primary,
                        borderColor: theme.colors.background.accent,
                        color: theme.colors.text.primary
                      }}
                    >
                      <option value="">All Partners</option>
                      {partners.map(partner => (
                        <option key={partner.partnerId} value={partner.partnerId}>{partner.partnerId}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={selectedPartnerForHistory}
                      onChange={(e) => setSelectedPartnerForHistory(e.target.value)}
                      className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      style={{
                        backgroundColor: theme.colors.background.primary,
                        borderColor: theme.colors.background.accent,
                        color: theme.colors.text.primary
                      }}
                      placeholder="Enter Partner ID to filter"
                    />
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <BiCalendar className="text-gray-400" />
                  <span className="text-sm text-gray-400">Start Date:</span>
                  <input
                    type="date"
                    value={historyFilters.startDate}
                    onChange={(e) => setHistoryFilters({...historyFilters, startDate: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme.colors.background.primary,
                      borderColor: theme.colors.background.accent,
                      color: theme.colors.text.primary
                    }}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">End Date:</span>
                  <input
                    type="date"
                    value={historyFilters.endDate}
                    onChange={(e) => setHistoryFilters({...historyFilters, endDate: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme.colors.background.primary,
                      borderColor: theme.colors.background.accent,
                      color: theme.colors.text.primary
                    }}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Status:</span>
                  <select
                    value={historyFilters.status}
                    onChange={(e) => setHistoryFilters({...historyFilters, status: e.target.value})}
                    className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme.colors.background.primary,
                      borderColor: theme.colors.background.accent,
                      color: theme.colors.text.primary
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <button
                  onClick={handleHistoryFilter}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Filter History
                </button>
              </div>
            </div>

            {/* History Table */}
            {settlementHistoryLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-3" style={{ color: theme.colors.text.secondary }}>Loading settlement history...</p>
                </div>
              </div>
            ) : settlementHistoryError ? (
              <div className="text-center py-8">
                <div className="text-red-500 text-4xl mb-2">⚠️</div>
                <p className="text-red-400">{settlementHistoryError}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <TableSection
                  data={prepareHistoryTableData()}
                  columns={historyTableColumns}
                  theme={theme}
                  pagination={{
                    currentPage: settlementHistory?.data?.current_page || currentPage,
                    perPage: settlementHistory?.data?.per_page || perPage,
                    total: settlementHistory?.data?.total || 0
                  }}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handlePerPageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default InternalSettlement;
